// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// RxJS
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
// NGRX
import { select, Store } from '@ngrx/store';
import { Update } from '@ngrx/entity';
import { AppState } from '../../../../../../core/reducers';
// CRUD
import { LayoutUtilsService, MessageType } from '../../../../../../core/_base/crud';
// Services and Models
import {
  Pharmacy,
  PharmacyActionTypes,
  PharmacyGroupService,
  PharmacyOnServerCreated,
  PharmacyOnServerUpdated,
  selectPharmacyById,
  selectPharmacyError,
  selectPharmacyLatestSuccessfullAction,
} from '../../../../../../core/lists';
import { currentUser } from 'src/app/core/auth';
import { TranslateService } from '@ngx-translate/core';
import { skip, switchMap } from 'rxjs/operators';
import { GeneralValidationMessagesService } from '../../../../../../core/services/general-validation-messages.service';
import { NavigationService } from '../../../../../../core/services/navigation.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-pharmacy-edit',
  templateUrl: './pharmacy-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PharmacyEditComponent implements OnInit, OnDestroy {
  // Public properties
  pharmacy: Pharmacy;
  pharmacyId$: Observable<number>;
  companyId: number;
  oldPharmacy: Pharmacy;
  loadingSubject = new BehaviorSubject<boolean>(true);
  loading$: Observable<boolean>;
  pharmacyForm: FormGroup;
  hasFormErrors = false;
  isViewMode: boolean;
  clicked: boolean;
  filteredGroups: Observable<any[]>;
  isViewMode$: Observable<boolean>;
  // sticky portlet header margin
  private headerMargin: number;
  // Subscriptions
  private subscriptions: Subscription[] = [];
  urbanizationValue;
  Address1Value;
  Address2Value;
  cityValue;
  zipValue;
  params: any;
  /**
   * Component constructor
   *
   * @param navigationService
   * @param store
   * @param activatedRoute
   * @param router
   * @param pharmacyFB
   * @param pharmacyGroupService
   * @param layoutUtilsService
   * @param translate
   * @param cdr
   * @param globalService
   */
  constructor(
    private navigationService: NavigationService,
    private store: Store<AppState>,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private pharmacyFB: FormBuilder,
    private pharmacyGroupService: PharmacyGroupService,
    private layoutUtilsService: LayoutUtilsService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private globalService: GeneralValidationMessagesService,
  ) {}

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit() {
    // this line due to make the navigation service return to pharmacies list if there is no ->
    // navigation stack saved before in special cases
    this.navigationService.setDefaultRoot('/lists/pharmacies');

    this.activatedRoute.queryParams.subscribe((params) => {
      this.params = params;
    });
    const userSub = this.store.pipe(select(currentUser)).subscribe((res) => {
      if (!res) {
        this.router.navigateByUrl('/lists/pharmacies');
      }
      this.companyId = res.companyID;
    });
    const errorSubscription = this.store
      .pipe(select(selectPharmacyError), skip(1))
      .subscribe((res) => {
        if (res) {
          this.layoutUtilsService.showActionNotification(
            this.translate.instant('GLOBAL.UNSPECIFIED_ERROR'),
            MessageType.Update,
            3000,
            true,
            false,
          );
          this.clicked = false;
          this.loadingSubject.next(false);
        }
      });
    this.subscriptions.push(errorSubscription);
    const successSubscription = this.store
      .pipe(select(selectPharmacyLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          let successMsg;
          let msgType;
          switch (res.action_type) {
            case PharmacyActionTypes.PharmacyCreated:
              successMsg = this.translate.instant('PHARMACY.EDIT.ADD_MESSAGE');
              msgType = MessageType.Create;
              break;
            case PharmacyActionTypes.PharmacyUpdated:
              successMsg = this.translate.instant('PHARMACY.EDIT.UPDATE_MESSAGE');
              msgType = MessageType.Update;
              break;
          }
          if (successMsg) {
            this.layoutUtilsService.showActionNotification(successMsg, msgType, 3000, true, false);
            this.goBack();
          }
        }
      });
    this.subscriptions.push(successSubscription);
    this.subscriptions.push(userSub);
    this.loading$ = this.loadingSubject.asObservable();
    this.loadingSubject.next(true);
    const routeSub = this.activatedRoute.params.subscribe((params) => {
      const id = params.id;
      if (id && id > 0) {
        this.store.pipe(select(selectPharmacyById(id))).subscribe((result) => {
          if (result) {
            this.pharmacy = result;
            this.loadPharmacy();
            return;
          }
          const pharmacyById = this.globalService
            .getEntitiesById('pharmacies', id)
            .subscribe((res) => {
              if (res) {
                this.pharmacy = res.data;
                this.loadPharmacy();
                this.cdr.detectChanges();
                return;
              }
            });
          this.subscriptions.push(pharmacyById);
        });
      } else {
        this.pharmacy = new Pharmacy();
        this.loadPharmacy();
      }
      this.cdr.detectChanges();
    });
    this.subscriptions.push(routeSub);
    // sticky portlet header
    window.onload = () => {
      const style = getComputedStyle(document.getElementById('kt_header'));
      this.headerMargin = parseInt(style.height, 0);
    };
  }

  loadPharmacy() {
    if (!this.pharmacy) {
      this.goBack();
      return;
    }
    this.pharmacyId$ = of(this.pharmacy.id);
    this.oldPharmacy = Object.assign({}, this.pharmacy);
    this.initPharmacy();
  }

  /**
   * On destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Init pharmacy
   */
  initPharmacy() {
    this.createForm();
    this.loadingSubject.next(false);
    if (!this.pharmacy.id) {
      this.isViewMode = false;
    } else this.isViewMode = this.activatedRoute.url[`_value`][1]?.path === 'view';
    this.isViewMode$ = of(this.isViewMode);
  }

  /**
   * Create form
   */
  createForm() {
    this.pharmacyForm = this.pharmacyFB.group({
      name: [this.pharmacy.name, Validators.required],
      email: [this.pharmacy.email, Validators.email],
      phone: [
        this.pharmacy.phone && this.pharmacy.phone !== ''
          ? '(' +
            this.pharmacy.phone.substring(0, 3) +
            ') ' +
            this.pharmacy.phone.substring(3, 6) +
            '-' +
            this.pharmacy.phone.substring(6, 10)
          : '',
        Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/),
      ],
      fax: [
        this.pharmacy.fax && this.pharmacy.fax !== ''
          ? '(' +
            this.pharmacy.fax.substring(0, 3) +
            ') ' +
            this.pharmacy.fax.substring(3, 6) +
            '-' +
            this.pharmacy.fax.substring(6, 10)
          : '',
        Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/),
      ],
      preferred_communication_method: [this.pharmacy.preferred_communication_method],
      urbanization: [this.pharmacy.urbanization, Validators.maxLength(48)],
      city: [
        this.pharmacy.city,
        Validators.compose([Validators.required, Validators.maxLength(30)]),
      ],
      state: [this.pharmacy.state, Validators.required],
      zip: [
        this.pharmacy.zip,
        Validators.compose([Validators.required, Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/)]),
      ],
      address1: [
        this.pharmacy.address_1,
        Validators.compose([Validators.required, Validators.maxLength(48)]),
      ],
      address2: [this.pharmacy.address_2, Validators.maxLength(48)],
      referral_info: [this.pharmacy.referral_info],
      dea: [this.pharmacy.dea],
      npi: [this.pharmacy.npi],
      pharmacy_group: [this.pharmacy.pharmacy_group, Validators.required],
      note: [this.pharmacy.note],
      is_active: [this.pharmacy.is_active ? this.pharmacy.is_active : false],
    });
    this.urbanizationValue = this.pharmacy.urbanization;
    this.Address1Value = this.pharmacy.address_1;
    this.Address2Value = this.pharmacy.address_2;
    this.cityValue = this.pharmacy.city;
    this.zipValue = this.pharmacy.zip;
    if (this.activatedRoute.url[`_value`][1]?.path === 'view') {
      this.pharmacyForm.disable();
    } else {
      const autocompleteSub = this.pharmacyForm
        .get('pharmacy_group')
        .valueChanges.pipe(
          switchMap((value) =>
            value && value.length >= 3
              ? this.pharmacyGroupService.searchPharmacyGroups(value)
              : of([]),
          ),
        )
        .subscribe((result) => {
          this.filteredGroups = result.data ? of(result.data) : of([]);
          this.cdr.detectChanges();
        });
      this.subscriptions.push(autocompleteSub);
    }
  }

  goBack() {
    this.loadingSubject.next(false);
    this.navigationService.goBack();
  }

  
  /**
   * Reset
   */
  reset() {
    this.pharmacy = Object.assign({}, this.oldPharmacy);
    this.createForm();
    this.pharmacyForm.controls[`entity_type`].enable();
    this.hasFormErrors = false;
    this.pharmacyForm.markAsPristine();
    this.pharmacyForm.markAsUntouched();
    this.pharmacyForm.updateValueAndValidity();
  }

  onSumbit() {
    this.hasFormErrors = false;
    this.clicked = true;
    const controls = this.pharmacyForm.controls;
    /** check form */
    if (this.pharmacyForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      if (controls.pharmacy_group.value && !controls.pharmacy_group.value.id) {
        controls.pharmacy_group.setErrors({ required: true });
      }
      this.hasFormErrors = true;
      return;
    } else if (controls.pharmacy_group.value && !controls.pharmacy_group.value.id) {
      controls.pharmacy_group.setErrors({ required: true });
      return;
    }
    // tslint:disable-next-line:prefer-const
    const editedPharmacy = this.preparePharmacy();
    if (editedPharmacy.id > 0) {
      this.updatePharmacy(editedPharmacy);
      return;
    }
    this.addPharmacy(editedPharmacy);
  }

  /**
   * Returns object for saving
   */
  preparePharmacy(): Pharmacy {
    const controls = this.pharmacyForm.controls;
    const _pharmacy = new Pharmacy();
    _pharmacy.id = this.pharmacy.id;
    _pharmacy.name = controls.name.value;
    _pharmacy.email = controls.email.value;
    _pharmacy.phone =
      controls.phone.value !== null
        ? controls.phone.value.replace(/\D/g, '')
        : controls.phone.value;
    _pharmacy.fax =
      controls.fax.value !== null ? controls.fax.value.replace(/\D/g, '') : controls.fax.value;
    _pharmacy.preferred_communication_method = controls.preferred_communication_method.value;
    _pharmacy.city = controls.city.value;
    _pharmacy.state = controls.state.value ? controls.state.value.toUpperCase() : '';
    _pharmacy.zip = controls.zip.value;
    _pharmacy.address_1 = controls.address1.value;
    _pharmacy.address_2 = controls.address2.value;
    _pharmacy.referral_info = controls.referral_info.value;
    _pharmacy.dea = controls.dea.value;
    _pharmacy.npi = controls.npi.value;
    _pharmacy.pharmacy_group = controls.pharmacy_group.value;
    _pharmacy.note = controls.note.value;
    _pharmacy.urbanization = controls.urbanization.value;
    _pharmacy.is_active = controls.is_active.value;
    for (const [key, value] of Object.entries(_pharmacy)) {
      if (typeof value === 'string') {
        _pharmacy[key] = value.trim();
      }
    }
    return _pharmacy;
  }

  /**
   * Add pharmacy
   *
   * @param _pharmacy
   */
  addPharmacy(_pharmacy: Pharmacy) {
    this.loadingSubject.next(true);
    this.store.dispatch(new PharmacyOnServerCreated({ pharmacy: _pharmacy }));
  }

  /**
   * Update pharmacy
   *
   * @param _pharmacy
   */
  updatePharmacy(_pharmacy: Pharmacy) {
    this.loadingSubject.next(true);

    const updatePharmacy: Update<Pharmacy> = {
      id: _pharmacy.id,
      changes: _pharmacy,
    };

    this.store.dispatch(
      new PharmacyOnServerUpdated({
        partialPharmacy: updatePharmacy,
        pharmacy: _pharmacy,
      }),
    );
  }

  /**
   * Checking control validation
   *
   * @param controlName
   * @param validationType
   */
  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.pharmacyForm.controls[controlName];
    if (!control) {
      return false;
    }

    return control.hasError(validationType) && (control.dirty || control.touched);
  }

  /**
   * Returns component title
   */
  getComponentTitle() {
    let result = this.translate.instant('PHARMACY.NEW_PHARMACY');
    if (!this.pharmacy || !this.pharmacy.id) {
      return result;
    } else if (this.activatedRoute.url[`_value`][1]?.path === 'view') {
      result = `${this.translate.instant('PHARMACY.VIEW_PHARMACY')} - ${this.pharmacy.name}`;
      return result;
    }

    result = `${this.translate.instant('PHARMACY.EDIT_PHARMACY')} - ${this.pharmacy.name}`;
    return result;
  }

  /**
   * Close alert
   *
   */
  onAlertClose() {
    this.hasFormErrors = false;
  }

  /**
   * Returns status string
   */
  getStatusString(): string {
    switch (this.pharmacyForm.get('is_active').value) {
      case true:
        return this.translate.instant('GLOBAL.ACTIVE');
      case false:
        return this.translate.instant('GLOBAL.INACTIVE');
    }
    return '';
  }

  editPharmacy(id) {
    this.navigationService.navigateTo('lists/pharmacies/edit/' + id);
  }

  displayFn(pharmacyGroup): string {
    return pharmacyGroup ? pharmacyGroup.name : '';
  }

  getTitle() {
    let result = 'New Pharmacy';
    if (!this.pharmacy || !this.pharmacy.id) {
      return result;
    }
    result = `Pharmacy - ${this.pharmacy.name}`;
    return result;
  }
  putDash(e) {
    let x = e;
    if (x.length === 6 && x.charAt(5) !== '-') {
      x = x.slice(0, 5) + '-' + x.charAt(x.length - 1);
    }
    return x;
  }
}
